import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Calendar, dateFnsLocalizer, Views } from "react-big-calendar";
import { format, parse, startOfWeek, getDay, startOfMonth, endOfMonth, addDays, subDays } from "date-fns";
import { enUS } from "date-fns/locale";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, useAuth } from "@/contexts/AuthContext";
import Navbar from "@/components/Navbar";
import EventDialog from "@/components/EventDialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { CalendarDays, Plus, Link2, Copy, MapPin } from "lucide-react";
import { VisitChip, VisitDayPanel, NewVisitDialog } from "@/components/CalendarVisits";
import { TYPE_COLORS, TYPE_LABELS } from "@/lib/calendarUtils";

const localizer = dateFnsLocalizer({ format, parse, startOfWeek: () => startOfWeek(new Date(), { locale: enUS }), getDay, locales: { "en-US": enUS } });
const sel = "h-9 rounded-sm border border-[#0B132B]/15 bg-white px-2 text-xs";

function Legend() {
  return (
    <div className="flex flex-wrap gap-3 text-[11px] text-[#6C757D]" data-testid="calendar-legend">
      {Object.keys(TYPE_LABELS).map((k) => <span key={k} className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: TYPE_COLORS[k] }} /> {TYPE_LABELS[k]}</span>)}
    </div>
  );
}

function FeedLink() {
  const [url, setUrl] = useState(null);
  useEffect(() => { api.get("/calendar/feed-token").then(({ data }) => setUrl(data.url)).catch(() => {}); }, []);
  const gen = async () => { const { data } = await api.post("/calendar/feed-token"); setUrl(data.url); toast.success("Subscription link ready"); };
  const copy = () => { navigator.clipboard?.writeText(url); toast.success("Copied — paste into Apple/Google/Outlook “subscribe by URL”"); };
  return (
    <div className="flex items-center gap-2 text-xs" data-testid="calendar-feed">
      <Link2 size={13} className="text-[#6C757D]" />
      {url ? <><span className="truncate max-w-[220px] text-[#6C757D]">{url}</span><button onClick={copy} className="text-[#0B132B] underline inline-flex items-center gap-1" data-testid="calendar-feed-copy"><Copy size={11} /> Copy</button><button onClick={gen} className="text-[#6C757D] underline" data-testid="calendar-feed-rotate">Reset</button></>
        : <button onClick={gen} className="text-[#0B132B] underline" data-testid="calendar-feed-generate">Get subscribe link (Apple / Outlook / Google)</button>}
    </div>
  );
}

export default function CalendarPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [view, setView] = useState(Views.MONTH);
  const [date, setDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [owners, setOwners] = useState([]);
  const [filters, setFilters] = useState({ owner_id: "", type: "", include_tasks: true, include_milestones: true });
  const [dialog, setDialog] = useState({ open: false, event: null, initial: null });
  const [qs, setQs] = useSearchParams();
  useEffect(() => { if (qs.get("new") === "1") { setDialog({ open: true, event: null, initial: null }); qs.delete("new"); setQs(qs, { replace: true }); } }, [qs, setQs]);

  const range = useMemo(() => {
    if (view === Views.MONTH) return { start: subDays(startOfMonth(date), 7), end: addDays(endOfMonth(date), 7) };
    if (view === Views.WEEK) return { start: startOfWeek(date), end: addDays(startOfWeek(date), 7) };
    if (view === Views.AGENDA) return { start: date, end: addDays(date, 30) };
    return { start: new Date(date.toDateString()), end: addDays(new Date(date.toDateString()), 1) };
  }, [view, date]);

  const [visits, setVisits] = useState([]);
  const [visitDay, setVisitDay] = useState(null);
  const [visitDlg, setVisitDlg] = useState({ open: false, date: null });
  const [dealers, setDealers] = useState([]);
  useEffect(() => { api.get("/dealers").then(({ data }) => setDealers(data || [])).catch(() => {}); }, []);
  const loadVisits = useCallback(() => {
    api.get("/calendar/visits", { params: { start: format(range.start, "yyyy-MM-dd"), end: format(range.end, "yyyy-MM-dd"), owner_id: filters.owner_id || undefined } }).then(({ data }) => setVisits(data || [])).catch(() => {});
  }, [range, filters.owner_id]);
  useEffect(() => { loadVisits(); }, [loadVisits]);
  const shiftDay = (n) => setVisitDay((d) => format(addDays(new Date(d + "T12:00:00"), n), "yyyy-MM-dd"));
  const DateHeader = ({ date: d, label }) => {
    const key = format(d, "yyyy-MM-dd");
    const todays = visits.filter((v) => v.day === key && v.log?.status !== "removed");
    return (
      <div className="text-right">
        <button type="button" onClick={() => setVisitDay(key)} className="text-xs hover:underline" data-testid={`cal-day-${key}`}>{label}</button>
        {todays.length > 0 && <div className="text-left mt-0.5">{todays.slice(0, 3).map((v) => <VisitChip key={v.id + v.day} v={v} onClick={() => setVisitDay(key)} />)}{todays.length > 3 && <div className="text-[10px] text-[#6C757D]">+{todays.length - 3} more stops</div>}</div>}
      </div>
    );
  };

  const load = useCallback(() => {
    api.get("/calendar/events", { params: { start: range.start.toISOString(), end: range.end.toISOString(), owner_id: filters.owner_id || undefined, type: filters.type || undefined, include_tasks: filters.include_tasks, include_milestones: filters.include_milestones } })
      .then(({ data }) => setEvents(data.map((e) => ({ ...e, startDate: new Date(e.start), endDate: new Date(e.end) }))))
      .catch(() => toast.error("Could not load calendar"));
  }, [range, filters]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/calendar/owners").then(({ data }) => setOwners(data || [])).catch(() => {}); }, []);

  const onSelectEvent = (e) => {
    if (e.kind === "task") return nav(`/tasks?task=${e.task_id}`);
    if (e.kind === "milestone") return nav(`/onboarding/${e.onboarding_id}`);
    setDialog({ open: true, event: e, initial: null });
  };
  const onSelectSlot = ({ start, end }) => setDialog({ open: true, event: null, initial: { start: start.toISOString(), end: (end > start ? end : new Date(start.getTime() + 3600e3)).toISOString() } });
  const eventPropGetter = (e) => ({ style: { backgroundColor: TYPE_COLORS[e.type] || TYPE_COLORS.other, borderRadius: 2, border: 0, opacity: e.status === "cancelled" ? 0.45 : 1, fontSize: 12 } });

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <Navbar />
      <main className="max-w-[1400px] mx-auto px-3 sm:px-6 py-6" data-testid="calendar-page">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#D4AF37]">Schedule</div>
            <h1 className="text-3xl sm:text-4xl font-black tracking-tight text-[#0B132B] flex items-center gap-2"><CalendarDays size={28} /> Calendar</h1>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setVisitDlg({ open: true, date: visitDay })} className="h-10 rounded-sm border-[#D4AF37]/60" data-testid="calendar-plan-visit-btn"><MapPin size={14} className="mr-1" /> Plan a visit</Button>
            <Button onClick={() => setDialog({ open: true, event: null, initial: null })} className="h-10 rounded-sm bg-[#0B132B] hover:bg-[#1C2541] text-white" data-testid="calendar-new-btn"><Plus size={14} className="mr-1" /> New appointment</Button>
          </div>
        </div>
        {visitDay && <VisitDayPanel day={visitDay} visits={visits} onClose={() => setVisitDay(null)} onChanged={loadVisits} onAdd={() => setVisitDlg({ open: true, date: visitDay })} onPrev={() => shiftDay(-1)} onNext={() => shiftDay(1)} />}
        <Card className="p-3 mb-3 rounded-sm border-[#E9ECEF] bg-white flex flex-wrap items-center gap-2" data-testid="calendar-filters">
          {owners.length > 1 && <select value={filters.owner_id} onChange={(e) => setFilters({ ...filters, owner_id: e.target.value })} className={sel} data-testid="calendar-owner-filter"><option value="">All calendars I can see</option>{owners.map((o) => <option key={o.id} value={o.id}>{o.id === user?.id ? "My calendar" : o.name}</option>)}</select>}
          <select value={filters.type} onChange={(e) => setFilters({ ...filters, type: e.target.value })} className={sel} data-testid="calendar-type-filter"><option value="">All activity types</option>{Object.keys(TYPE_LABELS).filter((k) => !["task", "milestone"].includes(k)).map((k) => <option key={k} value={k}>{TYPE_LABELS[k]}</option>)}</select>
          <label className="text-xs flex items-center gap-1 text-[#6C757D]"><input type="checkbox" checked={filters.include_tasks} onChange={(e) => setFilters({ ...filters, include_tasks: e.target.checked })} className="accent-[#0B132B]" data-testid="calendar-show-tasks" /> Task due dates</label>
          <label className="text-xs flex items-center gap-1 text-[#6C757D]"><input type="checkbox" checked={filters.include_milestones} onChange={(e) => setFilters({ ...filters, include_milestones: e.target.checked })} className="accent-[#0B132B]" data-testid="calendar-show-milestones" /> Pipeline milestones</label>
          <div className="ml-auto"><FeedLink /></div>
        </Card>
        <Card className="p-2 sm:p-4 rounded-sm border-[#E9ECEF] bg-white" data-testid="calendar-grid">
          <div className="h-[72vh] min-h-[520px] text-sm">
            <Calendar localizer={localizer} events={events} startAccessor="startDate" endAccessor="endDate" allDayAccessor="all_day" titleAccessor="title"
              view={view} onView={setView} date={date} onNavigate={setDate} views={[Views.MONTH, Views.WEEK, Views.DAY, Views.AGENDA]}
              selectable onSelectSlot={onSelectSlot} onSelectEvent={onSelectEvent} eventPropGetter={eventPropGetter} popup
              components={{ month: { dateHeader: DateHeader } }}
              tooltipAccessor={(e) => [e.title, e.dealer_name, e.owner_name].filter(Boolean).join(" · ")} />
          </div>
          <div className="mt-3"><Legend /></div>
        </Card>
      </main>
      <EventDialog open={dialog.open} event={dialog.event} initial={dialog.initial} onClose={() => setDialog({ open: false, event: null, initial: null })} onSaved={load} />
      <NewVisitDialog open={visitDlg.open} initialDate={visitDlg.date} owners={owners} dealers={dealers} onClose={() => setVisitDlg({ open: false, date: null })} onSaved={loadVisits} />
    </div>
  );
}
